import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { IAccount } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useAdminCalendarMeetingTranscriptsMutation } from '../../../queries/Admin/Calendar/useAdminCalendarMeetingTranscriptsMutation';
import { useAdminZoomMeetingsByAccountQuery } from '../../../queries/Admin/Zoom/useAdminZoomMeetingsByAccountQuery';
import {
	IAccountScheduledMeeting,
	ICalendarMeetingWithTranscripts,
	ITranscriptMatch,
} from '../../../viewmodels/CalendarViewModels';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { BackArrowIcon } from '../../../web/components/svgs/icons/BackArrowIcon';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { AskLevDialog } from '../AskLevDialog';
import { MeetingNoteRecordingFlow } from '../MeetingNoteRecordingFlow';
import { styleSheet } from './styles';

interface IProps {
	isOpen: boolean;
	onClose: () => void;
	accountId: string;
	accountName?: string;
	/** Optional full account object to avoid a redundant API call */
	account?: IAccount;
}

type ViewState = 'meetings' | 'transcripts' | 'recording';

export const FindRecordingsModal: React.FC<IProps> = ({ isOpen, onClose, accountId, accountName, account }) => {
	const [viewState, setViewState] = React.useState<ViewState>('meetings');
	const [selectedMeeting, setSelectedMeeting] = React.useState<IAccountScheduledMeeting | null>(null);
	const [expandedTranscripts, setExpandedTranscripts] = React.useState<Set<number>>(new Set());
	const [matchedMeetingData, setMatchedMeetingData] = React.useState<ICalendarMeetingWithTranscripts | null>(null);
	const [recordingTranscript, setRecordingTranscript] = React.useState<ITranscriptMatch | null>(null);
	const [askLevTranscript, setAskLevTranscript] = React.useState<ITranscriptMatch | null>(null);

	const userSession = useUserSession();
	const meetingsQuery = useAdminZoomMeetingsByAccountQuery(accountId, { enabled: isOpen });
	const transcriptsMutation = useAdminCalendarMeetingTranscriptsMutation();

	// Reset state when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			setViewState('meetings');
			setSelectedMeeting(null);
			setExpandedTranscripts(new Set());
			setMatchedMeetingData(null);
			setRecordingTranscript(null);
			setAskLevTranscript(null);
			transcriptsMutation.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const handleMeetingClick = React.useCallback(
		async (meeting: IAccountScheduledMeeting) => {
			if (!meeting.hostUserEmail || !meeting.scheduledStartDate) {
				return;
			}

			setSelectedMeeting(meeting);
			setViewState('transcripts');
			setExpandedTranscripts(new Set());
			setMatchedMeetingData(null);

			const meetingDate = new Date(meeting.scheduledStartDate);

			const result = await transcriptsMutation.mutateAsync({
				userId: meeting.hostUserId,
				accountId: userSession.account.id,
				userEmail: meeting.hostUserEmail,
				startDate: meetingDate,
				endDate: meetingDate,
			});

			// Find the matching meeting from the API response
			const meetingStartTime = new Date(meeting.scheduledStartDate).getTime();
			const matchedMeeting = result.meetings?.find(m => {
				if (m.meeting?.startTime) {
					const responseStartTime = new Date(m.meeting.startTime).getTime();
					if (Math.abs(responseStartTime - meetingStartTime) < 60000) {
						return true;
					}
				}
				if (m.meeting?.summary && meeting.meetingTitle) {
					return m.meeting.summary.toLowerCase() === meeting.meetingTitle.toLowerCase();
				}
				return false;
			});

			if (matchedMeeting) {
				setMatchedMeetingData(matchedMeeting);
			} else if (result.meetings && result.meetings.length === 1) {
				setMatchedMeetingData(result.meetings[0]);
			} else {
				const firstWithTranscripts = result.meetings?.find(m => m.transcriptMatches && m.transcriptMatches.length > 0);
				setMatchedMeetingData(firstWithTranscripts || null);
			}
		},
		[userSession.account.id, transcriptsMutation]
	);

	const handleBackToMeetings = React.useCallback(() => {
		setViewState('meetings');
		setSelectedMeeting(null);
		setExpandedTranscripts(new Set());
		setMatchedMeetingData(null);
		transcriptsMutation.reset();
	}, [transcriptsMutation]);

	const handleBackToTranscripts = React.useCallback(() => {
		setViewState('transcripts');
		setRecordingTranscript(null);
	}, []);

	const handleStartRecording = React.useCallback((transcript: ITranscriptMatch) => {
		setRecordingTranscript(transcript);
		setViewState('recording');
	}, []);

	const handleRecordingComplete = React.useCallback(() => {
		setViewState('transcripts');
		setRecordingTranscript(null);
	}, []);

	const toggleTranscriptExpansion = React.useCallback((index: number) => {
		setExpandedTranscripts(prev => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	}, []);

	const getMatchScoreStyle = (score: number): StyleDeclarationValue => {
		if (score >= 70) return styleSheet.matchScoreHigh;
		if (score >= 50) return styleSheet.matchScoreMedium;
		return styleSheet.matchScoreLow;
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	};

	const renderMeetingsList = () => {
		if (meetingsQuery.isLoading) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
					<span className={css(styleSheet.loadingText)}>Loading meetings...</span>
				</div>
			);
		}

		if (meetingsQuery.error) {
			return (
				<div className={css(styleSheet.errorMessage)}>Error loading meetings. Please try again or contact support.</div>
			);
		}

		const meetings = meetingsQuery.data?.meetings || [];

		if (meetings.length === 0) {
			return <div className={css(styleSheet.noDataMessage)}>No meetings found for this account.</div>;
		}

		return (
			<div>
				{meetings.map(meeting => (
					<div
						key={meeting.scheduledMeetingId}
						className={css(styleSheet.meetingCard)}
						onClick={() => handleMeetingClick(meeting)}
					>
						<div className={css(styleSheet.meetingCardHeader)}>
							<div>
								<div className={css(styleSheet.meetingTitle)}>{meeting.meetingTitle || 'Untitled Meeting'}</div>
								<div className={css(styleSheet.meetingMeta)}>
									{meeting.scheduledStartDate && <span>{formatDate(meeting.scheduledStartDate)}</span>}
									{meeting.hostUserEmail && (
										<>
											<span className={css(styleSheet.transcriptDivider)}>•</span>
											<span>Host: {meeting.hostUserEmail}</span>
										</>
									)}
								</div>
							</div>
							{meeting.hostUserEmail && <div className={css(styleSheet.viewRecordingsLink)}>View Recordings →</div>}
						</div>
					</div>
				))}
			</div>
		);
	};

	const renderTranscriptsView = () => {
		if (transcriptsMutation.isPending) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
					<span className={css(styleSheet.loadingText)}>Searching for transcripts...</span>
				</div>
			);
		}

		if (transcriptsMutation.error) {
			return (
				<div className={css(styleSheet.errorMessage)}>
					Error loading transcripts. Please try again or contact support.
				</div>
			);
		}

		const transcriptMatches: ITranscriptMatch[] = matchedMeetingData?.transcriptMatches || [];

		if (transcriptMatches.length === 0) {
			return <div className={css(styleSheet.noDataMessage)}>No transcripts or recordings found for this meeting.</div>;
		}

		return (
			<div>
				{transcriptMatches.map((match, index) => {
					const isExpanded = expandedTranscripts.has(index);
					return (
						<div key={index} className={css(styleSheet.transcriptCard)}>
							<div className={css(styleSheet.transcriptCardHeader)}>
								<div className={css(styleSheet.fullWidth)}>
									<div className={css(styleSheet.transcriptTopic)}>{match.meetingTopic || 'Untitled Meeting'}</div>
									<div className={css(styleSheet.transcriptMeta)}>
										<strong>Duration:</strong> {match.durationMinutes} minutes
										<span className={css(styleSheet.transcriptDivider)}>•</span>
										<strong>Match Score:</strong>{' '}
										<span className={css(styleSheet.matchScore, getMatchScoreStyle(match.matchScore))}>
											{Math.round(match.matchScore)}%
										</span>
									</div>
									{match.matchReasons && match.matchReasons.length > 0 && (
										<div className={css(styleSheet.transcriptReasons)}>{match.matchReasons.join(', ')}</div>
									)}
								</div>
								{match.transcriptContent && (
									<button className={css(styleSheet.recordMeetingNoteLink)} onClick={() => handleStartRecording(match)}>
										Record Meeting Note
									</button>
								)}
							</div>

							{isExpanded && match.transcriptContent && (
								<div className={css(styleSheet.expandedContent)}>
									<div className={css(styleSheet.transcriptContent)}>{match.transcriptContent}</div>
								</div>
							)}

							{(match.transcriptContent || match.playUrl) && (
								<div className={css(styleSheet.transcriptCardFooter)}>
									{match.transcriptContent && (
										<button
											className={css(styleSheet.viewTranscriptLink)}
											onClick={() => toggleTranscriptExpansion(index)}
										>
											{isExpanded ? 'Hide Transcript' : 'View Full Transcript'}
										</button>
									)}
									{match.transcriptContent && match.playUrl && <span className={css(styleSheet.pipeDivider)}>|</span>}
									{match.playUrl && (
										<button
											className={css(styleSheet.viewTranscriptLink)}
											onClick={() => window.open(match.playUrl, '_blank')}
										>
											Play Recording
										</button>
									)}
									{match.transcriptContent && (
										<>
											<span className={css(styleSheet.pipeDivider)}>|</span>
											<button className={css(styleSheet.viewTranscriptLink)} onClick={() => setAskLevTranscript(match)}>
												Ask Lev
											</button>
										</>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	const renderRecordingView = () => {
		if (!recordingTranscript || !matchedMeetingData) return null;

		return (
			<MeetingNoteRecordingFlow
				transcript={recordingTranscript}
				meetingWithTranscripts={matchedMeetingData}
				preSelectedAccount={account}
				onComplete={handleRecordingComplete}
			/>
		);
	};

	const getDialogTitle = () => {
		if (viewState === 'recording') return 'Record Meeting Note';
		if (viewState === 'transcripts') {
			return `Transcripts: ${selectedMeeting?.meetingTitle || 'Meeting'}`;
		}
		return `Recordings for ${accountName || 'Account'}`;
	};

	const getDialogStyle = () => {
		if (viewState === 'recording') return styleSheet.dialogWide;
		return styleSheet.dialog;
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={open => {
					if (!open) {
						onClose();
					}
				}}
			>
				<DialogPopup styles={[getDialogStyle()]}>
					<DialogHeader>
						<DialogTitle>{getDialogTitle()}</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogBody]}>
						{viewState === 'transcripts' && (
							<button className={css(styleSheet.backButton)} onClick={handleBackToMeetings}>
								<BackArrowIcon />
								Back to Meetings
							</button>
						)}
						{viewState === 'recording' && (
							<button className={css(styleSheet.backButton)} onClick={handleBackToTranscripts}>
								<BackArrowIcon />
								Back to Transcripts
							</button>
						)}
						{viewState === 'meetings' && renderMeetingsList()}
						{viewState === 'transcripts' && renderTranscriptsView()}
						{viewState === 'recording' && renderRecordingView()}
					</DialogBody>
				</DialogPopup>
			</Dialog>

			{askLevTranscript && (
				<AskLevDialog
					open={!!askLevTranscript}
					onOpenChange={open => {
						if (!open) setAskLevTranscript(null);
					}}
					transcriptContent={askLevTranscript.transcriptContent!}
					meetingTopic={askLevTranscript.meetingTopic}
					attendeeNames={matchedMeetingData?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown')}
				/>
			)}
		</>
	);
};
